import {IMockFn, IMocks} from '@graphql-tools/mock';
import {IntrospectionNamedTypeRef, IntrospectionObjectType} from 'graphql';

import GraphQLIntrospectionResult from 'graphql-client/schema/GraphQLIntrospectionResult';
import {typeIsAbstract, typeIsPossiblyInaccessibleInfo} from 'graphql-client/schema/util/SchemaOps';
import {BIOENTITY_TYPE_TO_TYPENAME} from 'registry/graphql/GraphQLBioentityTypes';

import APIIDPrefixToTypename from '../../../graphql-fragments/APIIDPrefixToTypename.json5';
import overrideMocksInOrder from './overrideMocks';

function defaultFloat(): number {
  return Math.random() * 2 - 1;
}

function defaultInt(): number {
  return Math.round(defaultFloat() * 2000000000);
}

function defaultString(): string {
  return `<autogenerated>${defaultFloat()}`;
}

function defaultBool(): boolean {
  return defaultFloat() > 0;
}

const DEFAULT_SCALAR_MOCKS = {
  StrictString: defaultString,
  StrictInt: defaultInt,
  StrictFloat: defaultFloat,
  StrictBoolean: defaultBool,
  JSONScalar: () => ({}),
  StrictDecimal: () => defaultFloat().toString(),
};

function pickRandomElement<E>(list: Array<E>): E {
  const sample = Math.floor(Math.random() * list.length);
  return list[sample];
}

function generateIDOrUndefined(typename: string): {id: string} | undefined {
  const extraMappings = [
    // These Node types are SSIs over the custom entity and share the same 'bfi' prefix, so cannot be added to
    // the APIIDPrefixToTypename.json5 file.
    {apiIdPrefix: 'bfi', __typename: 'ComplexPolymerNode'},
    {apiIdPrefix: 'bfi', __typename: 'OligoConjugateNode'},
    {apiIdPrefix: 'bfi', __typename: 'OligoDuplexNode'},
    {apiIdPrefix: 'bfi', __typename: 'EquipmentNode'},
  ];
  // Attempt to generate an ID using APIIDPrefixToTypename, but fallback to undefined if it doesn't work.
  const record = [...extraMappings, ...APIIDPrefixToTypename.mapping].find(
    (rec) => rec.__typename === typename
  );
  return record ? {id: `${record.apiIdPrefix}_autogen${defaultInt()}`} : undefined;
}

function _makeTypeResolverExcludingInaccessibleInfo(
  possibleTypes: ReadonlyArray<IntrospectionNamedTypeRef<IntrospectionObjectType>>
): IMockFn {
  const allowedTypes = possibleTypes
    .filter((type) => type.name !== 'InaccessibleInfoNode' && !typeIsAbstract(type))
    .map(({name}) => name);

  return () => {
    const __typename = pickRandomElement(allowedTypes);
    return {__typename, ...generateIDOrUndefined(__typename)};
  };
}

/**
 * Some components error out when encountering a concrete `InaccessibleInfo` in place of a
 * `{X}OrInaccessibleInfoNode` union type; this causes all ancestor component tests to randomly fail.
 *
 * To avoid that, this set of default mocks preserves the random concrete type selection behavior of Apollo's
 * `@graphql-tools/mock` but excludes `InaccessibleInfoNode` from the possible types.
 */
const DEFAULT_EXCLUDE_INACCESSIBLE_INFO_MOCKS = GraphQLIntrospectionResult.__schema.types.reduce(
  (mocks, typeDef) => {
    if (typeDef.name === 'InaccessibleInfoNode') {
      return mocks;
    }
    if (typeDef.kind === 'OBJECT') {
      mocks[typeDef.name] = _makeTypeResolverExcludingInaccessibleInfo(
        // @ts-expect-error TODO(BNCH-12772): Clean these typedefs up - under a deadline at the moment
        typeDef.possibleTypes ?? [{name: typeDef.name}]
      );
    } else if (typeIsPossiblyInaccessibleInfo(typeDef) && typeIsAbstract(typeDef)) {
      mocks[typeDef.name] = _makeTypeResolverExcludingInaccessibleInfo(typeDef.possibleTypes);
    }
    return mocks;
  },
  {} as IMocks
);

const bioentityTypenames = Object.values(BIOENTITY_TYPE_TO_TYPENAME);
// Autogenerating bioentityAliasInfos can cause problems with registry validation, because
// we fail if any bioentity that has aliases with a registry owner is not actually registered.
// eslint-disable-next-line benchlint/no-spread-reduce
const bioentityDefaultMocks = bioentityTypenames.reduce(
  (mocks, typename) => ({
    ...mocks,
    [typename]: () => ({bioentityAliasInfos: () => []}),
  }),
  {}
);

const DEFAULT_MODEL_TYPE_MOCKS = {
  AnalyticsDashboardNode: () => ({
    // Autogenerating queries and layouts for analytics dashboards is problematic because they will not
    // be synced. The queries will not be the same in the layout and queries array, which breaks a large
    // assumption with dashboards. If a dashboard with queries is needed, use a custom resolver with our
    // correctly layed out fixtures in AnalyticsDashboardFixtures.ts.
    analyticsQueries: () => [],
    layoutRows: () => [],
    // TODO(BNCH-16497) Remove empty default resolvers for parameter configs and values
    parameterConfigs: () => [],
    parameterValues: () => [],
  }),
  TagSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    iconId: () => 'notdef',
  }),
  BoxSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    iconId: () => 'notdef',
  }),
  ContainerSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    iconId: () => 'notdef',
  }),
  LocationSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    iconId: () => 'notdef',
  }),
  PlateSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    iconId: () => 'notdef',
  }),
  BatchSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    archiveRecord: () => null,
  }),
  RequestSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    archiveRecord: () => null,
  }),
  ResultSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    archiveRecord: () => null,
    publishedDataTableColumnsJson: () => [],
  }),
  RunSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
    archiveRecord: () => null,
  }),
  SampleGroupSchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
  }),
  StudySchemaNode: () => ({
    fieldsJson: () => [],
    allFieldsJson: () => [],
  }),
  BatchNode: () => ({
    iconId: () => 'notdef',
  }),
  BasicFolderItemNode: () => ({
    iconId: () => 'notdef',
  }),
  MixtureNode: () => ({
    iconId: () => 'notdef',
  }),
  MoleculeNode: () => ({
    iconId: () => 'notdef',
  }),
  ProteinNode: () => ({
    iconId: () => 'notdef',
  }),
  SequenceNode: () => ({
    iconId: () => 'notdef',
  }),
  MixtureInstructionsNode: () => ({
    noteJson: () => ({
      noteParts: [],
      selectors: [],
    }),
  }),
  ...bioentityDefaultMocks,
};

const DEFAULT_QUERY_FIELD_MOCKS = {
  Query: () => ({
    foo: () => "Hi! I'm a fake foo",
  }),
};

const DEFAULT_MOCKS = overrideMocksInOrder(
  DEFAULT_SCALAR_MOCKS,
  DEFAULT_EXCLUDE_INACCESSIBLE_INFO_MOCKS,
  DEFAULT_MODEL_TYPE_MOCKS,
  DEFAULT_QUERY_FIELD_MOCKS
);

export default DEFAULT_MOCKS;
